type FetchOptions = RequestInit & {
   retry?: RetryOptions;
};

interface RetryOptions {
   maxRetries?: number;
   retryableStatuses?: number[];
   globalTimeout?: number;
}

const DEFAULT_RETRY_OPTIONS: Required<RetryOptions> = {
   maxRetries: 3,
   retryableStatuses: [408, 429, 500, 502, 503, 504],
   globalTimeout: 10_000, // By default Vercel functions have a 15s timeout
};

export async function robustFetch(url: string, options?: FetchOptions): Promise<Response> {
   const retryConfig = { ...DEFAULT_RETRY_OPTIONS, ...options?.retry };
   const globalController = new AbortController();
   const globalTimeoutId = setTimeout(() => globalController.abort(), retryConfig.globalTimeout);

   try {
      return await attemptRequest({
         url,
         options: { ...options, signal: globalController.signal },
         config: retryConfig,
         attempt: 0,
      });
   } finally {
      clearTimeout(globalTimeoutId);
   }
}

interface AttemptRequestOptions {
   url: string;
   options?: FetchOptions;
   config: Required<RetryOptions>;
   attempt: number;
}

async function attemptRequest({
   url,
   options,
   config,
   attempt,
}: AttemptRequestOptions): Promise<Response> {
   try {
      const response = await fetch(url, options);

      if (config.retryableStatuses.includes(response.status) && attempt < config.maxRetries) {
         console.warn(`Retrying request to ${url} due to status ${response.status}`);
         await waitForNextRetry(attempt);
         return attemptRequest({ url, options, config, attempt: attempt + 1 });
      }

      return response;
   } catch (error) {
      if (isRetryableError(error) && attempt < config.maxRetries) {
         console.warn(`Retrying request to ${url} due to error: ${error.message}`);
         await waitForNextRetry(attempt);
         return attemptRequest({ url, options, config, attempt: attempt + 1 });
      }

      throw error;
   }
}

function isRetryableError(error: unknown): error is Error {
   return error instanceof TypeError;
}

async function waitForNextRetry(attempt: number): Promise<void> {
   const baseDelay = 300;
   const delay = baseDelay * Math.pow(2, attempt);
   await new Promise(resolve => {
      setTimeout(resolve, delay);
   });
}
